/**
 * FELVORA — Vertical LFPIORPI: Avisos SPPLD (F4).
 *
 * Bandeja mensual + generación XML por fracción + informe en ceros + panel 24h con
 * reloj + subida de acuse + vista de conservación desde la operación.
 *
 * feb-2026 · iter 08-02 — Fase PLD PURA: sólo se muestra Fr. V (compraventa e
 * intermediación) en la UI. Los mapas y helpers para V Bis y XV quedan en código
 * comentado por si el backend aún los envía; cuando se reactiven esas fracciones se
 * descomenta la línea correspondiente.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Send, FileText, Clock, CheckCircle2, AlertCircle, Download,
  Upload, RefreshCw, ShieldAlert, ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { giroNombreCorto } from "@/lib/actividad_vulnerable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useMiActividad } from "@/hooks/useMiActividad";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

// feb-2026 · iter 08-02 — Solo Fr. V se expone en UI (fase PLD PURA).
const FRACCION_LABEL = {
  V: "Fr. V (compraventa e intermediación)",
  // V_bis: "Fr. V Bis (desarrollo)",              // pendiente
  // XV:    "Fr. XV (arrendamiento — por predio)", // pendiente
};

// ago-2026 · Bloque A — cualquier fracción del Art. 17 se etiqueta sola: las que no tienen
// texto propio arriba se escriben con su romano ("V_bis" → "Fr. V Bis").
const etiquetaFraccion = (frac) =>
  FRACCION_LABEL[frac] || (frac ? `Fr. ${String(frac).replace(/_bis$/i, " Bis")}` : "—");

function mesPrevio() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function EstadoBadge({ estatus }) {
  const map = {
    pendiente: "bg-slate-100 text-slate-700",
    generado: "bg-emerald-100 text-emerald-800",
    incompleto: "bg-amber-100 text-amber-800",
    transmitido: "bg-blue-100 text-blue-800",
    acusado: "bg-emerald-200 text-emerald-900",
    cancelado: "bg-red-100 text-red-800",
  };
  return <Badge className={`capitalize ${map[estatus] || "bg-slate-100"}`}>{estatus}</Badge>;
}

function TipoBadge({ tipo }) {
  const label = { mensual: "Mensual", en_ceros: "En ceros", "24h": "24h" }[tipo] || tipo;
  const color = tipo === "24h" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700";
  return <Badge className={color}>{label}</Badge>;
}

/** Reloj de 24h: descuenta desde `vencimiento_24h_iso`. Retorna estado color-coded. */
function Reloj24h({ vencimientoIso }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!vencimientoIso) return null;
  const rem = new Date(vencimientoIso).getTime() - now;
  const abs = Math.abs(rem);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  const s = Math.floor((abs % 60_000) / 1000);
  const cls = rem < 0 ? "bg-red-100 text-red-900" : (rem < 6 * 3_600_000 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800");
  return (
    <Badge className={cls} data-testid="reloj-24h">
      <Clock className="mr-1 h-3 w-3 inline" />
      {rem < 0 ? "VENCIDO hace " : ""}
      {`${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`}
    </Badge>
  );
}

export default function Avisos() {
  const { user } = useAuth();
  const act = useMiActividad();
  const giroCorto = act.nombreCorto || giroNombreCorto(user?.giro);
  const [periodo, setPeriodo] = useState(mesPrevio());
  const [bandeja, setBandeja] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [tab, setTab] = useState("mensual");
  const [detalle, setDetalle] = useState(null);
  const [inmuebles, setInmuebles] = useState([]);
  const [ceroFraccion, setCeroFraccion] = useState("V");
  const [ceroInmueble, setCeroInmueble] = useState("");
  const [inmuebleXV, setInmuebleXV] = useState("");
  const [uploading, setUploading] = useState(false);

  const cargar = () => {
    api.get(`/inmobiliaria/avisos/bandeja/${periodo}`).then((r) => setBandeja(r.data)).catch((e) => {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error cargando bandeja");
    });
    api.get(`/inmobiliaria/avisos?periodo=${periodo}`).then((r) => setAvisos(r.data)).catch(() => {});
  };

  useEffect(() => {
    cargar();
    api.get("/inmobiliaria/inmuebles").then((r) => setInmuebles(r.data)).catch(() => {});
  }, [periodo]);

  const generarMensual = async (fraccion, inmueble_id = null) => {
    try {
      const r = await api.post("/inmobiliaria/avisos/generar", { periodo, fraccion, inmueble_id });
      toast.success(`Aviso ${fraccion} ${r.data.estatus === "incompleto" ? "GENERADO INCOMPLETO — hay faltantes" : "generado"}`);
      cargar();
      setDetalle(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error");
    }
  };

  const generarEnCeros = async () => {
    try {
      const body = { periodo, fraccion: ceroFrac };
      if (ceroFrac === "XV") {
        if (!ceroInmueble) return toast.error("Fr. XV: elige inmueble");
        body.inmueble_id = ceroInmueble;
      }
      const r = await api.post("/inmobiliaria/avisos/en-ceros", body);
      toast.success("Informe en ceros generado");
      cargar();
      setDetalle(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error");
    }
  };

  const regenerar = async (id) => {
    try {
      const r = await api.post(`/inmobiliaria/avisos/${id}/regenerar`);
      toast.success(r.data.estatus === "incompleto" ? "Aún hay faltantes" : "Aviso regenerado sin recaptura");
      cargar();
      setDetalle(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const transmitir = async (id) => {
    try {
      await api.post(`/inmobiliaria/avisos/${id}/transmitir`);
      toast.success("Marcado como transmitido — sube el acuse del portal SPPLD");
      cargar(); setDetalle((d) => d ? { ...d, estatus: "transmitido" } : d);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const descargar = async (id, nombre) => {
    try {
      const r = await api.get(`/inmobiliaria/avisos/${id}/xml`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = nombre || "aviso.xml"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error descargando XML"); }
  };

  const subirAcuse = async (id, file, folio) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folio_acuse", folio || "");
      await api.post(`/inmobiliaria/avisos/${id}/acuse`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Acuse subido · obligación día 17 marcada cumplida");
      cargar();
      const r = await api.get(`/inmobiliaria/avisos/${id}`);
      setDetalle(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error subiendo acuse"); }
    finally { setUploading(false); }
  };

  const totales = bandeja?.totales || { V: 0, V_bis: 0, XV: 0, avisos_generados: 0 };
  // Bloque A — las fracciones que la pantalla opera son las de MI actividad. Inmobiliaria
  // conserva la fase PLD PURA (solo Fr. V en pantalla, V Bis y XV siguen apagadas): así el
  // cambio no reactiva nada que ya estaba deliberadamente oculto.
  const fraccionesUi = act.giro === "inmobiliaria"
    ? ["V"]
    : (act.fraccionesRaw.length ? act.fraccionesRaw : ["V"]);
  // Si el usuario no ha elegido, o eligió una fracción que no es de su actividad, se usa
  // la primera de SU catálogo (para inmobiliaria sigue siendo "V").
  const ceroFrac = fraccionesUi.includes(ceroFraccion) ? ceroFraccion : fraccionesUi[0];
  const reportables = bandeja?.reportables || { V: [], V_bis: [], XV: [] };
  const inmueblesXV = useMemo(() => {
    const ids = new Set((reportables.XV || []).map((r) => r.inmueble_id));
    return inmuebles.filter((i) => ids.has(i.id));
  }, [reportables.XV, inmuebles]);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="avisos-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <Send className="mr-2 inline h-6 w-6 text-emerald-700" /> Avisos SPPLD · {giroCorto}
          </h1>
          <p className="text-sm text-slate-500">
            LFPIORPI {act.articulo} — mensual (día 17) + 24h + en ceros. Conservación 10 años.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="text" data-testid="input-periodo" value={periodo}
            onChange={(e) => setPeriodo(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            placeholder="AAAAMM" className="w-32"
          />
          <Button variant="outline" onClick={cargar} data-testid="btn-refresh">
            <RefreshCw className="mr-1 h-4 w-4" /> Actualizar
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="mensual" data-testid="tab-mensual">Mensual del periodo</TabsTrigger>
          <TabsTrigger value="24h" data-testid="tab-24h">Aviso 24h</TabsTrigger>
        </TabsList>

        <TabsContent value="mensual" className="space-y-3">
          {/* Contadores — feb-2026 · iter 08-02: sólo Fr. V en fase PLD PURA. V_bis y XV
              se recontratan al reactivar sus verticales. */}
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">{etiquetaFraccion(fraccionesUi[0]).split(" (")[0]} reportables</p>
              <p className="text-2xl font-semibold" data-testid="cnt-v">{totales[fraccionesUi[0]] || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">{act.bienes} con reporte</p>
              <p className="text-2xl font-semibold" data-testid="cnt-predios">{inmueblesXV.length}</p>
              <p className="text-xs text-slate-500 mt-1">{act.bienes.toLowerCase()} distintos</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-xs text-slate-500">Avisos generados</p>
              <p className="text-2xl font-semibold" data-testid="cnt-avisos">{totales.avisos_generados}</p>
            </CardContent></Card>
          </div>

          {/* Generador — feb-2026 · iter 08-02: sólo Fr. V. V_bis y XV pendientes de
              reactivar sus verticales (el backend sigue soportando ambos por API). */}
          <Card>
            <CardHeader><CardTitle className="text-base">Generar avisos del periodo</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {fraccionesUi.map((frac) => (
                <div key={frac} className="flex items-center justify-between border-b border-slate-100 py-2">
                  <div>
                    <p className="font-semibold text-sm">{etiquetaFraccion(frac)}</p>
                    <p className="text-xs text-slate-500">
                      {(reportables[frac] || []).length} operación(es) reportable(s).
                    </p>
                  </div>
                  {(reportables[frac] || []).length > 0 ? (
                    <Button data-testid={`btn-gen-${frac}`} onClick={() => generarMensual(frac)}>
                      <FileText className="mr-1 h-4 w-4" /> Generar XML
                    </Button>
                  ) : (
                    <p className="text-xs italic text-slate-400">Genera informe en ceros abajo si aplica.</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Informe en ceros */}
          <Card>
            <CardHeader><CardTitle className="text-base">Informe en ceros</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-2">
                Si el mes no tuvo operaciones objeto de aviso para una fracción, genera el informe en ceros.
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="text-xs">Fracción</label>
                  <Select value={ceroFrac} onValueChange={setCeroFraccion}>
                    <SelectTrigger className="w-56" data-testid="select-cero-fraccion">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Bloque A — las fracciones son las de MI actividad (catálogo). */}
                      {fraccionesUi.map((f) => (
                        <SelectItem key={f} value={f}>{etiquetaFraccion(f)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Sección "elige inmueble" de Fr. XV oculta con las opciones. */}
                <Button variant="outline" data-testid="btn-en-ceros" onClick={generarEnCeros}>
                  <FileText className="mr-1 h-4 w-4" /> Generar en ceros
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lista de avisos del periodo */}
          <Card>
            <CardHeader><CardTitle className="text-base">Avisos del periodo</CardTitle></CardHeader>
            <CardContent>
              <div className="divide-y divide-slate-200">
                {avisos.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Sin avisos generados aún.</p>}
                {avisos.map((a) => (
                  <div key={a.id} className="py-3 cursor-pointer hover:bg-slate-50 flex flex-wrap items-center justify-between gap-2"
                       data-testid={`aviso-row-${a.id}`}
                       onClick={() => setDetalle(a)}>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {etiquetaFraccion(a.fraccion)} · {a.periodo}
                      </p>
                      <p className="text-xs text-slate-500">
                        {a.total_operaciones} op(s) · {mxn(a.monto_reportado_total_mxn)}
                        {a.inmueble_id ? ` · predio ${a.inmueble_id.slice(0, 8)}…` : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <TipoBadge tipo={a.tipo} />
                      <EstadoBadge estatus={a.estatus} />
                      {a.tipo === "24h" && <Reloj24h vencimientoIso={a.vencimiento_24h_iso} />}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="24h" className="space-y-3">
          <Card>
            <CardHeader><CardTitle className="text-base">
              <ShieldAlert className="mr-1 inline h-4 w-4 text-red-700" />
              Aviso 24 horas
            </CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">
                Se genera manualmente por cumplimiento_inmo desde el detalle de una operación con
                match LPB o indicio. Aquí verás todos los 24h del periodo. El reloj indica el tiempo
                restante para transmitir en el portal.
              </p>
              <div className="divide-y divide-slate-200">
                {avisos.filter((a) => a.tipo === "24h").length === 0 && (
                  <p className="py-4 text-center text-sm text-slate-500">
                    Sin avisos 24h en el periodo. Genera uno desde la operación con match LPB.
                  </p>
                )}
                {avisos.filter((a) => a.tipo === "24h").map((a) => (
                  <div key={a.id} className="py-3 flex flex-wrap items-center justify-between gap-2"
                       data-testid={`aviso24h-${a.id}`}>
                    <div>
                      <p className="font-semibold text-sm">{etiquetaFraccion(a.fraccion)}</p>
                      <p className="text-xs text-slate-500">
                        {a.origen === "auto_lpb" ? "Match LPB" : "Manual"} · op {a.operacion_origen_id?.slice(0, 8) || ""} · {mxn(a.monto_reportado_total_mxn)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Reloj24h vencimientoIso={a.vencimiento_24h_iso} />
                      <EstadoBadge estatus={a.estatus} />
                      <Button size="sm" variant="outline" data-testid={`btn-abrir-24h-${a.id}`}
                              onClick={() => setDetalle(a)}>
                        Detalle
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detalle */}
      <Dialog open={!!detalle} onOpenChange={(v) => !v && setDetalle(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detalle && (<span>
                {etiquetaFraccion(detalle.fraccion)} · {detalle.periodo} <TipoBadge tipo={detalle.tipo} />
              </span>)}
            </DialogTitle>
          </DialogHeader>
          {detalle && (
            <DetalleAviso
              aviso={detalle}
              onDescargar={() => descargar(detalle.id, detalle.nombre_archivo)}
              onTransmitir={() => transmitir(detalle.id)}
              onSubirAcuse={(file, folio) => subirAcuse(detalle.id, file, folio)}
              onRegenerar={() => regenerar(detalle.id)}
              uploading={uploading}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetalleAviso({ aviso, onDescargar, onTransmitir, onSubirAcuse, onRegenerar, uploading }) {
  const [archivo, setArchivo] = useState(null);
  const [folio, setFolio] = useState("");
  const puedeTransmitir = aviso.estatus === "generado";
  const puedeAcusar = aviso.estatus === "transmitido";
  const incompleto = aviso.estatus === "incompleto";

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap gap-2">
        <EstadoBadge estatus={aviso.estatus} />
        {aviso.tipo === "24h" && <Reloj24h vencimientoIso={aviso.vencimiento_24h_iso} />}
        <Badge variant="secondary">{aviso.total_operaciones} op(s)</Badge>
        <Badge variant="outline">Total: {mxn(aviso.monto_reportado_total_mxn)}</Badge>
        {aviso.pre_validado && <Badge className="bg-blue-100 text-blue-800">Pre-validado</Badge>}
      </div>

      <div className="rounded border border-slate-200 p-3 text-xs">
        <p><strong>Regla F3:</strong> monto reportado = TOTAL con IVA (el umbral se evaluó SIN IVA).</p>
        {aviso.hash_sha256 && (
          <p className="mt-1"><strong>Hash SHA-256:</strong> <span className="font-mono">{aviso.hash_sha256}</span></p>
        )}
        {aviso.nombre_archivo && <p><strong>Archivo:</strong> {aviso.nombre_archivo}</p>}
        {aviso.folio_acuse && <p><strong>Folio SPPLD (acuse):</strong> {aviso.folio_acuse}</p>}
      </div>

      {incompleto && (
        <div className="rounded border border-amber-400 bg-amber-50 p-3">
          <p className="font-semibold text-amber-900 mb-1">
            <AlertCircle className="mr-1 inline h-4 w-4" />
            Aviso INCOMPLETO — completa los faltantes en la fuente original y regenera
          </p>
          <ul className="ml-4 list-disc text-xs">
            {(aviso.faltantes || []).map((f, i) => (
              <li key={i} data-testid={`faltante-${i}`}>
                <span className="capitalize">{f.seccion}</span>
                {f.parte_rol ? ` [${f.parte_rol}]` : ""}: {f.campos.join(", ")}
                {f.link && (
                  <a href={`#${f.link}`} className="ml-2 text-blue-700 underline">
                    ir a {f.link}
                  </a>
                )}
              </li>
            ))}
          </ul>
          <Button size="sm" className="mt-2" onClick={onRegenerar} data-testid="btn-regenerar">
            <RefreshCw className="mr-1 h-4 w-4" /> Regenerar (sin recaptura)
          </Button>
        </div>
      )}

      {/* Operaciones snap */}
      {(aviso.operaciones_snap || []).length > 0 && (
        <div>
          <p className="font-semibold mb-1">Operaciones incluidas ({aviso.operaciones_snap.length})</p>
          <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 border rounded">
            {aviso.operaciones_snap.map((o, i) => (
              <div key={o.operacion_id} className="p-2 text-xs" data-testid={`op-snap-${i}`}>
                <div className="flex justify-between">
                  <span className="font-mono">{o.operacion_id.slice(0, 8)}</span>
                  <span>{mxn(o.monto_reportado_mxn)}</span>
                </div>
                <p>
                  <span className="capitalize">{o.tipo_operacion}</span> · {o.fecha_operacion} ·
                  {" "}{o.partes?.length || 0} parte(s) ·
                  {o.objeto_de_aviso_por_acumulacion ? " Acumulación" : (o.objeto_de_aviso_individual ? " Individual" : "")}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {aviso.tipo === "en_ceros" && (
        <p className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs">
          Informe EN CEROS del periodo — no incluye operaciones. Registra la obligación
          cumplida en el semáforo día 17 al recibir el acuse del portal SPPLD.
        </p>
      )}

      {/* Acciones */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
        {aviso.archivo_key && (
          <Button variant="outline" onClick={onDescargar} data-testid="btn-descargar-xml">
            <Download className="mr-1 h-4 w-4" /> Descargar XML
          </Button>
        )}
        {puedeTransmitir && (
          <Button onClick={onTransmitir} data-testid="btn-transmitir">
            <Send className="mr-1 h-4 w-4" /> Marcar transmitido en portal SPPLD
          </Button>
        )}
        {puedeAcusar && (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-xs">Folio SPPLD (opcional)</label>
              <Input value={folio} onChange={(e) => setFolio(e.target.value)}
                     className="w-40" data-testid="input-folio-acuse" />
            </div>
            <div>
              <label className="text-xs">Acuse del portal</label>
              <Input type="file" onChange={(e) => setArchivo(e.target.files?.[0])}
                     data-testid="input-file-acuse" />
            </div>
            <Button disabled={!archivo || uploading}
                    onClick={() => onSubirAcuse(archivo, folio)}
                    data-testid="btn-subir-acuse">
              <Upload className="mr-1 h-4 w-4" /> Subir acuse
            </Button>
          </div>
        )}
        {aviso.estatus === "acusado" && (
          <Badge className="bg-emerald-200 text-emerald-900">
            <CheckCircle2 className="mr-1 h-3 w-3 inline" /> Ciclo completo · obligación día 17 cumplida
          </Badge>
        )}
      </div>

      {aviso.operacion_origen_id && (
        <div className="pt-2">
          <a
            href={`/inmobiliaria/operaciones?op=${aviso.operacion_origen_id}`}
            className="text-xs text-blue-700 underline inline-flex items-center"
          >
            <ExternalLink className="mr-1 h-3 w-3" />
            Ver operación de origen
          </a>
        </div>
      )}
    </div>
  );
}
